/**
 * @file 単元の対話 — 見取り図の単元名をタップして開く。状態・いつから・記録・教材・入試の出題。
 *
 * @what: 状態ボタン (未着手 → 取り組み中 → できた)、始めた日とできた日 (サーバが付ける)、
 *   ステップごとの水準、この単元に紐づいた教材 (タップで教材の対話)、日付つきの記録
 *   (取り組みの記録を unit_id で絞る) と「記録を足す」、取り込んだ入試問題へのリンク。
 * @why: 記録のタブが独立していると単元の進みを残しにくい。見取り図から単元を開いて、そこで
 *   状態と記録を残せるようにする (代表 2026-08-24)。
 * @failure: 描画と draft の更新のみ。記録の追加失敗は文言を出す。
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { formatLogDate, todayLocal } from "../api/dates";
import {
  STUDY_MATERIAL_STATUS_LABELS,
  STUDY_UNIT_STATUS_LABELS,
  newProgressLogId,
  type StudyProgressLog,
  type StudyProgressLogInput,
  type StudyUnitState,
  type StudyUnitStatus,
} from "../api/plans";
import { levelById, type AttainmentModel, type Unit } from "../model";
import { entrySummary, type CorpusEntry } from "../model/corpusIndex";
import type { RoadmapMaterial } from "./roadmapTypes";
import { StudyDialog } from "./StudyDialog";
import "./study.css";

const CORPUS_PREVIEW = 5;

function formatStamp(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

export function UnitDialog({
  model,
  unit,
  onClose,
  status,
  onToggleStatus,
  serverState,
  logs,
  onAddLog,
  logBusy,
  logError,
  materials,
  onOpenMaterial,
  corpusEntries,
  sourceHrefBase,
}: {
  model: AttainmentModel;
  unit: Unit;
  onClose: () => void;
  /** draft の状態。お子さんを選んでいなければ undefined (読むだけ)。 */
  status?: StudyUnitStatus;
  onToggleStatus?: () => void;
  /** サーバが持つ状態 (始めた日・できた日)。 */
  serverState?: StudyUnitState;
  logs: StudyProgressLog[];
  onAddLog?: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  logBusy: boolean;
  logError: string | null;
  /** この単元に紐づいた教材 (すべての時期)。 */
  materials: readonly RoadmapMaterial[];
  onOpenMaterial?: (materialId: string) => void;
  corpusEntries: CorpusEntry[];
  sourceHrefBase?: string;
}) {
  const [text, setText] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const domain = model.domains.find((d) => d.id === unit.domain);
  const stageLabel = (id: string) => model.stages.find((s) => s.id === id)?.period ?? id;
  const started = formatStamp(serverState?.started_at);
  const reached = formatStamp(serverState?.reached_at);
  const shownLogs = showAll ? logs : logs.slice(0, 3);
  const submit = async () => {
    const memo = text.trim();
    if (!memo || !onAddLog || logBusy) return;
    setAttempted(true);
    const created = await onAddLog({
      id: newProgressLogId(),
      date: todayLocal(),
      unit_id: unit.id,
      memo,
    });
    if (created) {
      setText("");
      setAttempted(false);
    }
  };
  const itemHref = (e: CorpusEntry) =>
    `${sourceHrefBase}/${encodeURIComponent(e.sourceId)}?item=${encodeURIComponent(e.item.id)}`;
  const first = corpusEntries[0];

  return (
    <StudyDialog title={unit.label} labelledBy={`study-unit-dialog-${unit.id}`} onClose={onClose}>
      <div className="study-dialog-grid">
        {domain ? <p className="study-dialog-sub">{domain.label}</p> : null}

        <section className="study-dialog-section" aria-label="状態">
          <div className="study-dialog-row">
            {status !== undefined && onToggleStatus ? (
              <button
                type="button"
                className={`study-status-btn study-status-btn--${status}`}
                onClick={onToggleStatus}
                aria-label={`${unit.label}の状態（${STUDY_UNIT_STATUS_LABELS[status]}）`}
                title="クリックで 未着手 → 取り組み中 → できた の順に切り替わります"
              >
                {STUDY_UNIT_STATUS_LABELS[status]}
              </button>
            ) : null}
            <span className="study-dialog-stamps">
              <span>始めた {started ?? "—"}</span>
              <span>できた {reached ?? "—"}</span>
            </span>
          </div>
          <p className="study-dialog-hint">
            状態は保存バーで保存すると、始めた日・できた日が自動で残ります。
          </p>
        </section>

        <section className="study-dialog-section" aria-label="ステップごとの水準">
          <h4 className="study-dialog-h">ステップごとの水準</h4>
          <ul className="study-dialog-levels">
            {model.stages.map((s) => {
              const lv = unit.expected[s.id];
              if (!lv) return null;
              const level = levelById(model, lv);
              return (
                <li key={s.id}>
                  <span className="study-dialog-level-stage">{s.period}</span>
                  <span className={`study-level study-level--${level.id}`}>{level.label}</span>
                  {unit.notes[s.id] ? <span className="study-dialog-level-note">{unit.notes[s.id]}</span> : null}
                </li>
              );
            })}
          </ul>
        </section>

        <section className="study-dialog-section" aria-label="教材">
          <h4 className="study-dialog-h">教材</h4>
          {materials.length > 0 ? (
            <ul className="study-dialog-materials">
              {materials.map((m) => (
                <li key={m.id}>
                  {onOpenMaterial ? (
                    <button
                      type="button"
                      className={`study-material-chip study-material-chip--${m.status} study-material-chip--btn`}
                      onClick={() => onOpenMaterial(m.id)}
                      title={STUDY_MATERIAL_STATUS_LABELS[m.status]}
                    >
                      {m.title}
                    </button>
                  ) : (
                    <span className={`study-material-chip study-material-chip--${m.status}`}>{m.title}</span>
                  )}
                  <span className="study-dialog-material-stage">
                    {m.stage_id ? stageLabel(m.stage_id) : "時期を決めていない"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="study-muted">この単元に紐づけた教材はありません。</p>
          )}
        </section>

        <section className="study-dialog-section" aria-label="記録">
          <h4 className="study-dialog-h">記録</h4>
          {logs.length > 0 ? (
            <ul className="study-material-note-list" aria-label={`${unit.label} の記録`}>
              {shownLogs.map((l) => (
                <li key={l.id}>
                  <span className="study-material-note-date">{formatLogDate(l.date)}</span>
                  <span className="study-material-note-text">{l.memo}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="study-muted">まだ記録がありません。</p>
          )}
          {logs.length > 3 ? (
            <button type="button" className="study-link-btn" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "最近の 3 件だけ見る" : `すべて見る（${logs.length} 件）`}
            </button>
          ) : null}
          {onAddLog ? (
            <div className="study-material-note-add">
              <input
                type="text"
                placeholder="記録を足す（今日の日付で残ります）"
                aria-label={`${unit.label} に記録を足す`}
                value={text}
                maxLength={500}
                onChange={(e) => setText(e.target.value)}
              />
              <button
                type="button"
                className="study-ghost-btn study-ghost-btn--sm"
                onClick={() => void submit()}
                disabled={!text.trim() || logBusy}
              >
                足す
              </button>
            </div>
          ) : null}
          {attempted && logError ? (
            <p className="study-material-lookup-error" role="alert">
              {logError}
            </p>
          ) : null}
        </section>

        {corpusEntries.length > 0 ? (
          <section className="study-dialog-section" aria-label="入試での出題">
            <h4 className="study-dialog-h">入試での出題 {corpusEntries.length} 問</h4>
            {unit.basis ? <p className="study-dialog-hint">{unit.basis}</p> : null}
            <ul className="study-corpus-list">
              {corpusEntries.slice(0, CORPUS_PREVIEW).map((e) => (
                <li key={`${e.sourceId}/${e.item.id}`} className="study-corpus-row">
                  <span className="study-corpus-school">{e.item.index["出典校"] ?? "出典なし"}</span>
                  <span className="study-corpus-summary">{entrySummary(e.item)}</span>
                  {sourceHrefBase ? (
                    <Link className="study-corpus-open" to={itemHref(e)}>
                      見る
                    </Link>
                  ) : null}
                </li>
              ))}
            </ul>
            {sourceHrefBase && first ? (
              <Link
                className="study-link-btn"
                to={`${sourceHrefBase}/${encodeURIComponent(first.sourceId)}?unit=${encodeURIComponent(unit.id)}`}
              >
                {unit.label} の出題をすべて見る（{corpusEntries.length} 問）
              </Link>
            ) : null}
          </section>
        ) : null}
      </div>
    </StudyDialog>
  );
}
